import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Star, ThumbsUp, Camera, CheckCircle, Leaf, MessageSquare, ChevronDown, ChevronUp, X, Upload, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import useUserStore from '@/store/userStore';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Star Rating Component
const StarRating = ({ rating, setRating, readonly = false, size = 'md' }) => {
  const [hover, setHover] = useState(0);
  const sizeClass = size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-7 h-7' : 'w-5 h-5';
  
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readonly}
          className={`${readonly ? 'cursor-default' : 'cursor-pointer'} transition-transform hover:scale-110`}
          onClick={() => !readonly && setRating(star)}
          onMouseEnter={() => !readonly && setHover(star)}
          onMouseLeave={() => !readonly && setHover(0)}
        >
          <Star
            className={`${sizeClass} ${
              star <= (hover || rating)
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-gray-300'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

// Single Review Card
const ReviewCard = ({ review, onHelpful }) => {
  const [expanded, setExpanded] = useState(false);
  const timeAgo = getTimeAgo(review.created_at);
  
  return (
    <Card className="p-5 md:p-6" data-testid={`review-${review._id}`}>
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <StarRating rating={review.rating} readonly size="sm" />
            {review.verified_purchase && (
              <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                <CheckCircle className="w-3 h-3" />
                Verified Purchase
              </span>
            )}
          </div>
          <h4 className="font-semibold text-lg">{review.title}</h4>
        </div>
        <span className="text-xs text-muted-foreground whitespace-nowrap">{timeAgo}</span>
      </div>
      
      <p className={`text-muted-foreground mb-4 ${!expanded && review.content.length > 200 ? 'line-clamp-3' : ''}`}>
        {review.content}
      </p>
      
      {review.content.length > 200 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-primary text-sm mb-4 flex items-center gap-1"
        >
          {expanded ? <><ChevronUp className="w-4 h-4" /> Show less</> : <><ChevronDown className="w-4 h-4" /> Read more</>}
        </button>
      )}
      
      {/* Tank Specs */}
      {review.tank_specs && Object.values(review.tank_specs).some(v => v) && (
        <div className="bg-secondary/50 rounded-lg p-3 mb-4">
          <p className="text-xs font-semibold text-muted-foreground mb-2">REVIEWER'S TANK SETUP</p>
          <div className="flex flex-wrap gap-2">
            {review.tank_specs.tank_size && (
              <span className="text-xs bg-white px-2 py-1 rounded-full border">🐠 {review.tank_specs.tank_size}</span>
            )}
            {review.tank_specs.lighting && (
              <span className="text-xs bg-white px-2 py-1 rounded-full border">💡 {review.tank_specs.lighting} Light</span>
            )}
            {review.tank_specs.co2 && (
              <span className="text-xs bg-white px-2 py-1 rounded-full border">🫧 CO2: {review.tank_specs.co2}</span>
            )}
            {review.tank_specs.substrate && (
              <span className="text-xs bg-white px-2 py-1 rounded-full border">🪴 {review.tank_specs.substrate}</span>
            )}
          </div>
        </div>
      )}
      
      {/* Photos */}
      {review.photos && review.photos.length > 0 && (
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
          {review.photos.map((photo, idx) => {
            // URLs are now /api/uploads/... which works directly
            const photoUrl = photo.startsWith('http') ? photo : photo;
            return (
              <img
                key={idx}
                src={photoUrl}
                alt={`Review photo ${idx + 1}`}
                className="w-20 h-20 md:w-24 md:h-24 object-cover rounded-lg flex-shrink-0"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            );
          })}
        </div>
      )}
      
      {/* Still Thriving Badge */}
      {review.still_thriving && (
        <div className="flex items-center gap-2 text-green-600 bg-green-50 px-3 py-2 rounded-lg mb-4">
          <Leaf className="w-4 h-4" />
          <span className="text-sm font-medium">Still thriving!</span>
          {review.still_thriving_updated_at && (
            <span className="text-xs text-green-500">
              Updated {getTimeAgo(review.still_thriving_updated_at)}
            </span>
          )}
        </div>
      )}
      
      {/* Helpful Button */}
      <div className="flex items-center justify-between pt-3 border-t">
        <button
          onClick={() => onHelpful(review._id)}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
        >
          <ThumbsUp className="w-4 h-4" />
          Helpful ({review.helpful_votes || 0})
        </button>
      </div>
    </Card>
  );
};

// Review Form
const ReviewForm = ({ productId, onSubmit }) => {
  const { user, isLoggedIn } = useUserStore();
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tankSpecs, setTankSpecs] = useState({
    tank_size: '',
    lighting: '',
    co2: '',
    substrate: ''
  });
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showTankSpecs, setShowTankSpecs] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const uploadFiles = async (files) => {
    if (files.length === 0) return;
    
    // Check if adding these would exceed 3 photos
    if (photos.length + files.length > 3) {
      toast.error('Maximum 3 photos allowed');
      return;
    }
    
    setUploading(true);
    const uploadedUrls = [];
    
    for (const file of files) {
      // Validate file type
      if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
        toast.error(`${file.name}: Invalid file type. Use JPEG, PNG, WebP, or GIF.`);
        continue;
      }
      
      // Validate file size (25MB max)
      if (file.size > 25 * 1024 * 1024) {
        toast.error(`${file.name}: File too large. Max 25MB.`);
        continue;
      }
      
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API}/reviews/upload-photo`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        if (response.data.success) {
          // Construct full URL for the uploaded photo
          const fullUrl = response.data.url;
          uploadedUrls.push(fullUrl);
        }
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    if (uploadedUrls.length > 0) {
      setPhotos(prev => [...prev, ...uploadedUrls].slice(0, 3));
      toast.success(`${uploadedUrls.length} photo(s) uploaded!`);
    }
    
    setUploading(false);
    // Clear the input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    await uploadFiles(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      await uploadFiles(files);
    }
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }
    if (!title.trim() || !content.trim()) {
      toast.error('Please fill in title and review');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/reviews`, {
        product_id: productId,
        rating,
        title: title.trim(),
        content: content.trim(),
        tank_specs: showTankSpecs ? tankSpecs : null,
        photos,
        user_email: isLoggedIn ? user?.email : null
      });
      
      if (response.data.success) {
        const pointsMsg = response.data.points_earned > 0 
          ? ` You earned ${response.data.points_earned} Plant Perks!`
          : '';
        toast.success(`Review submitted!${pointsMsg}`);
        onSubmit();
        // Reset form
        setRating(0);
        setTitle('');
        setContent('');
        setTankSpecs({ tank_size: '', lighting: '', co2: '', substrate: '' });
        setPhotos([]);
      }
    } catch (error) {
      toast.error('Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="p-5 md:p-6" data-testid="review-form">
      <h3 className="font-semibold text-lg mb-4">Write a Review</h3>
      
      {isLoggedIn && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 text-sm text-green-700">
          <span className="font-medium">🌿 Earn Plant Perks!</span> Get 10 perks for your review + 5 perks per photo (max 3 photos = 25 perks total). Only for verified purchases.
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Rating */}
        <div>
          <Label className="mb-2 block">Your Rating *</Label>
          <StarRating rating={rating} setRating={setRating} size="lg" />
        </div>
        
        {/* Title */}
        <div>
          <Label htmlFor="review-title">Review Title *</Label>
          <Input
            id="review-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Sum up your experience"
            className="mt-1"
            maxLength={100}
          />
        </div>
        
        {/* Content */}
        <div>
          <Label htmlFor="review-content">Your Review *</Label>
          <Textarea
            id="review-content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="What did you like or dislike? How's the plant doing in your tank?"
            className="mt-1 min-h-[120px]"
          />
        </div>
        
        {/* Tank Specs Toggle */}
        <div>
          <button
            type="button"
            onClick={() => setShowTankSpecs(!showTankSpecs)}
            className="flex items-center gap-2 text-sm text-primary hover:underline"
          >
            {showTankSpecs ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {showTankSpecs ? 'Hide tank specs' : 'Add your tank specs (helps others!)'}
          </button>
          
          {showTankSpecs && (
            <div className="grid grid-cols-2 gap-3 mt-3 p-4 bg-secondary/30 rounded-lg">
              <div>
                <Label className="text-xs">Tank Size</Label>
                <Select value={tankSpecs.tank_size} onValueChange={(v) => setTankSpecs({...tankSpecs, tank_size: v})}>
                  <SelectTrigger className="mt-1 h-9">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5 gallon">5 gallon</SelectItem>
                    <SelectItem value="10 gallon">10 gallon</SelectItem>
                    <SelectItem value="20 gallon">20 gallon</SelectItem>
                    <SelectItem value="29 gallon">29 gallon</SelectItem>
                    <SelectItem value="40 gallon">40 gallon</SelectItem>
                    <SelectItem value="55 gallon">55 gallon</SelectItem>
                    <SelectItem value="75+ gallon">75+ gallon</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Lighting</Label>
                <Select value={tankSpecs.lighting} onValueChange={(v) => setTankSpecs({...tankSpecs, lighting: v})}>
                  <SelectTrigger className="mt-1 h-9">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Low">Low</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">CO2</Label>
                <Select value={tankSpecs.co2} onValueChange={(v) => setTankSpecs({...tankSpecs, co2: v})}>
                  <SelectTrigger className="mt-1 h-9">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="None">None</SelectItem>
                    <SelectItem value="DIY">DIY</SelectItem>
                    <SelectItem value="Pressurized">Pressurized</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Substrate</Label>
                <Select value={tankSpecs.substrate} onValueChange={(v) => setTankSpecs({...tankSpecs, substrate: v})}>
                  <SelectTrigger className="mt-1 h-9">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Gravel">Gravel</SelectItem>
                    <SelectItem value="Sand">Sand</SelectItem>
                    <SelectItem value="Aquasoil">Aquasoil</SelectItem>
                    <SelectItem value="Dirted">Dirted</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>
        
        {/* Photo Upload */}
        <div className="space-y-3">
          <Label className="flex items-center gap-2">
            <Camera className="w-4 h-4" />
            Add Photos (Optional - up to 3)
          </Label>
          
          {/* Photo Preview Grid */}
          {photos.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {photos.map((photo, idx) => (
                <div key={idx} className="relative group">
                  <img
                    src={photo}
                    alt={`Upload ${idx + 1}`}
                    className="w-20 h-20 object-cover rounded-lg border"
                  />
                  <button
                    type="button"
                    onClick={() => removePhoto(idx)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          
          {/* Upload Area with Drag & Drop */}
          {photos.length < 3 && (
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                multiple
                onChange={handlePhotoUpload}
                className="hidden"
                id="photo-upload"
                data-testid="photo-upload-input"
              />
              <label
                htmlFor="photo-upload"
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`flex flex-col items-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer transition-all ${
                  isDragging 
                    ? 'border-primary bg-primary/10' 
                    : 'border-gray-300 hover:border-primary hover:bg-primary/5'
                } ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    <span className="text-sm">Uploading...</span>
                  </>
                ) : (
                  <>
                    <Upload className={`w-6 h-6 ${isDragging ? 'text-primary' : 'text-muted-foreground'}`} />
                    <span className="text-sm text-center">
                      {isDragging ? 'Drop your photos here!' : 'Click to upload or drag & drop'}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      JPEG, PNG, WebP or GIF • Max 25MB • {3 - photos.length} remaining
                    </span>
                    {isLoggedIn ? (
                      <span className="text-xs text-green-600 mt-1">
                        ✨ Earn +5 perks per photo (up to 15 perks)
                      </span>
                    ) : (
                      <span className="text-xs text-primary mt-1 text-center">
                        ✨ Sign up for Plant Perks to earn up to 25 perks for your review with photos!
                      </span>
                    )}
                  </>
                )}
              </label>
            </div>
          )}
        </div>
        
        <Button
          type="submit"
          disabled={submitting || rating === 0}
          className="w-full rounded-full"
        >
          {submitting ? 'Submitting...' : 'Submit Review'}
        </Button>
      </form>
    </Card>
  );
};

// Q&A Section
const QASection = ({ productId }) => {
  const [questions, setQuestions] = useState([]);
  const [newQuestion, setNewQuestion] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState({});
  const { user, isLoggedIn } = useUserStore();

  useEffect(() => {
    fetchQuestions();
  }, [productId]);

  const fetchQuestions = async () => {
    try {
      const response = await axios.get(`${API}/qa/product/${productId}`);
      setQuestions(response.data.questions || []);
    } catch (error) {
      console.error('Error fetching Q&A:', error);
    }
  };

  const handleSubmitQuestion = async (e) => {
    e.preventDefault();
    if (!newQuestion.trim()) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/qa?product_id=${productId}&question=${encodeURIComponent(newQuestion.trim())}${isLoggedIn ? `&user_email=${user?.email}` : ''}`);
      toast.success('Question submitted!');
      setNewQuestion('');
      fetchQuestions();
    } catch (error) {
      toast.error('Failed to submit question');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="qa-section">
      <h3 className="font-semibold text-lg flex items-center gap-2">
        <MessageSquare className="w-5 h-5" />
        Questions & Answers ({questions.length})
      </h3>
      
      {/* Ask Question Form */}
      <form onSubmit={handleSubmitQuestion} className="flex gap-2">
        <Input
          value={newQuestion}
          onChange={(e) => setNewQuestion(e.target.value)}
          placeholder="Have a question? Ask here..."
          className="flex-1"
        />
        <Button type="submit" disabled={submitting || !newQuestion.trim()} className="rounded-full">
          Ask
        </Button>
      </form>
      
      {/* Questions List */}
      {questions.length > 0 ? (
        <div className="space-y-3">
          {questions.map((q) => (
            <Card key={q._id} className="p-4">
              <button
                onClick={() => setExpanded({...expanded, [q._id]: !expanded[q._id]})}
                className="w-full text-left"
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="font-medium">Q: {q.question}</p>
                  {expanded[q._id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>
              </button>
              
              {expanded[q._id] && q.answers && q.answers.length > 0 && (
                <div className="mt-3 pt-3 border-t space-y-2">
                  {q.answers.map((a, idx) => (
                    <div key={idx} className={`p-3 rounded-lg ${a.is_seller ? 'bg-primary/10' : 'bg-secondary/50'}`}>
                      <p className="text-sm">
                        {a.is_seller && <span className="font-semibold text-primary">AquaPlants Plus: </span>}
                        {a.answer}
                      </p>
                    </div>
                  ))}
                </div>
              )}
              
              {expanded[q._id] && (!q.answers || q.answers.length === 0) && (
                <p className="mt-3 pt-3 border-t text-sm text-muted-foreground">No answers yet</p>
              )}
            </Card>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No questions yet. Be the first to ask!</p>
      )}
    </div>
  );
};

// Main Reviews Component
const ProductReviews = ({ productId }) => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ total_reviews: 0, average_rating: 0, rating_breakdown: {} });
  const [sortBy, setSortBy] = useState('newest');
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReviews();
  }, [productId, sortBy]);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/product/${productId}?sort_by=${sortBy}`);
      setReviews(response.data.reviews || []);
      setStats(response.data.stats || { total_reviews: 0, average_rating: 0, rating_breakdown: {} });
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleHelpful = async (reviewId) => {
    try {
      await axios.post(`${API}/reviews/${reviewId}/helpful`);
      fetchReviews();
    } catch (error) {
      console.error('Error marking helpful:', error);
    }
  };

  if (loading) {
    return <div className="py-8 text-center text-muted-foreground">Loading reviews...</div>;
  }

  return (
    <div className="space-y-8" data-testid="product-reviews">
      {/* Reviews Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="heading-font text-2xl md:text-3xl font-bold">Customer Reviews</h2>
          <div className="flex items-center gap-3 mt-2">
            <StarRating rating={Math.round(stats.average_rating)} readonly />
            <span className="font-semibold">{stats.average_rating.toFixed(1)}</span>
            <span className="text-muted-foreground">({stats.total_reviews} reviews)</span>
          </div>
        </div>
        
        <Button
          onClick={() => setShowForm(!showForm)}
          className="rounded-full"
          variant={showForm ? 'outline' : 'default'}
        >
          {showForm ? 'Cancel' : 'Write a Review'}
        </Button>
      </div>
      
      {/* Rating Breakdown */}
      {stats.total_reviews > 0 && (
        <div className="bg-secondary/30 rounded-xl p-4 md:p-6">
          <div className="space-y-2">
            {[5, 4, 3, 2, 1].map((star) => {
              const count = stats.rating_breakdown[star] || 0;
              const percentage = stats.total_reviews > 0 ? (count / stats.total_reviews) * 100 : 0;
              return (
                <div key={star} className="flex items-center gap-3">
                  <span className="w-8 text-sm font-medium">{star} ★</span>
                  <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-yellow-400 rounded-full transition-all"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="w-8 text-sm text-muted-foreground">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Review Form */}
      {showForm && (
        <ReviewForm productId={productId} onSubmit={() => { setShowForm(false); fetchReviews(); }} />
      )}
      
      {/* Sort & Reviews List */}
      {reviews.length > 0 && (
        <>
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">{reviews.length} reviews</p>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest First</SelectItem>
                <SelectItem value="rating">Highest Rated</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-4">
            {reviews.map((review) => (
              <ReviewCard key={review._id} review={review} onHelpful={handleHelpful} />
            ))}
          </div>
        </>
      )}
      
      {reviews.length === 0 && !showForm && (
        <Card className="p-8 text-center">
          <p className="text-muted-foreground mb-4">No reviews yet. Be the first to share your experience!</p>
          <Button onClick={() => setShowForm(true)} className="rounded-full">
            Write the First Review
          </Button>
        </Card>
      )}
      
      {/* Q&A Section */}
      <div className="pt-8 border-t">
        <QASection productId={productId} />
      </div>
    </div>
  );
};

// Helper function
function getTimeAgo(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
}

export default ProductReviews;
